import React from "react";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { Trash2, X } from "lucide-react";

/**
 * v2.48 — Reusable bulk-select toolbar.
 *
 * Lives at the top of any admin list. Shows:
 *  - "Select all" checkbox (indeterminate when partial)
 *  - Selected count
 *  - "Delete selected" CTA (calls onBulkDelete)
 *  - "Clear" to reset selection
 *
 * Caller wires up the actual selection state: `selectedIds` Set<string>,
 * `allIds` array of currently-visible IDs, `setSelectedIds` setter, and
 * `onBulkDelete` async handler.
 */
export function BulkSelectBar({
  selectedIds,
  allIds,
  setSelectedIds,
  onBulkDelete,
  busy = false,
  noun = "items",
  testidPrefix = "bulk",
  extraActions = null,
}) {
  const visibleIds = Array.isArray(allIds) ? allIds : [];
  const selectedSize = selectedIds.size;
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someSelected = visibleIds.some((id) => selectedIds.has(id));
  const indeterminate = someSelected && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      // Unselect all visible
      const next = new Set(selectedIds);
      visibleIds.forEach((id) => next.delete(id));
      setSelectedIds(next);
    } else {
      // Select all visible
      const next = new Set(selectedIds);
      visibleIds.forEach((id) => next.add(id));
      setSelectedIds(next);
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const confirmAndDelete = async () => {
    if (selectedSize === 0) return;
    if (!window.confirm(`Delete ${selectedSize} ${noun}? This cannot be undone.`)) return;
    await onBulkDelete(Array.from(selectedIds));
    clearSelection();
  };

  return (
    <div
      className={`sticky top-0 z-10 border ${selectedSize > 0 ? "border-[#0055FF] bg-[#EEF4FF]" : "border-zinc-200 bg-white"} rounded-sm px-3 py-2 flex items-center gap-3 mb-3`}
      data-testid={`${testidPrefix}-bar`}
    >
      <Checkbox
        checked={allSelected ? true : (indeterminate ? "indeterminate" : false)}
        onCheckedChange={toggleAll}
        data-testid={`${testidPrefix}-toggle-all`}
        aria-label="Select all"
      />
      <div className="text-[12px] font-semibold text-zinc-700 flex-1">
        {selectedSize === 0
          ? <>Select <b>{noun}</b> to delete in bulk</>
          : <>Selected: <span className="text-[#0044CC]">{selectedSize}</span> · of {visibleIds.length} visible</>}
      </div>
      {selectedSize > 0 && (
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearSelection}
            className="h-7 text-[11px] text-zinc-600 rounded-sm"
            data-testid={`${testidPrefix}-clear`}
          >
            <X size={11} className="mr-1" /> Clear
          </Button>
          {extraActions}
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={confirmAndDelete}
            className="h-7 text-[11px] bg-red-600 hover:bg-red-700 text-white rounded-sm"
            data-testid={`${testidPrefix}-delete`}
          >
            <Trash2 size={11} className="mr-1" /> {busy ? "Deleting…" : `Delete ${selectedSize}`}
          </Button>
        </>
      )}
    </div>
  );
}

/**
 * Per-row checkbox. Stops event propagation so clicking the checkbox does
 * NOT trigger the row's onClick (e.g. drawer-open).
 */
export function RowCheckbox({ id, selectedIds, setSelectedIds, testid }) {
  const checked = selectedIds.has(id);
  const toggle = (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    const next = new Set(selectedIds);
    if (checked) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  return (
    <div onClick={(e) => e.stopPropagation()} className="shrink-0">
      <Checkbox
        checked={checked}
        onCheckedChange={toggle}
        data-testid={testid || `row-check-${id}`}
        aria-label={`Select ${id}`}
      />
    </div>
  );
}

export default BulkSelectBar;
